import { ChangeDetectionStrategy, Component, ElementRef, computed, inject, input, linkedSignal, model, signal } from '@angular/core';
import { format, isSameYear, startOfDay } from 'date-fns';

import { IconComponent } from '../../../shared/icon/icon.component';
import { DateRangeCalendarComponent } from './date-range-calendar/date-range-calendar.component';
import { LocationSelectComponent } from './location-select/location-select.component';
import { SizeSelectComponent } from './size-select/size-select.component';
import type { Location } from '../../locations/location.model';
import type { Size } from '../../orders/order.model';
import type { SizeKey } from '../../orders/size.utils';
import {
  DATE_PRESETS,
  PLATFORM_OPTIONS,
  minSelectableDate,
  presetRange,
  type DatePresetKey,
  type PlatformFilter,
  type StatisticsFilters,
} from './statistics-filters.model';

export const formatDateRange = (from: Date, to: Date): string =>
  isSameYear(from, to)
    ? `${format(from, 'd MMM')} – ${format(to, 'd MMM yyyy')}`
    : `${format(from, 'd MMM yyyy')} – ${format(to, 'd MMM yyyy')}`;

@Component({
  selector: 'app-statistics-filters',
  imports: [IconComponent, DateRangeCalendarComponent, LocationSelectComponent, SizeSelectComponent],
  templateUrl: './statistics-filters.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'flex flex-wrap items-center gap-3',
    '(document:click)': 'onDocumentClick($event)',
    '(document:keydown.escape)': 'close()',
  },
})
export class StatisticsFiltersComponent {
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  readonly filters = model.required<StatisticsFilters>();
  readonly locations = input<readonly Location[]>([]);
  readonly sizes = input<readonly Size[]>([]);

  protected readonly presets = DATE_PRESETS;
  protected readonly platforms = PLATFORM_OPTIONS;
  protected readonly isOpen = signal(false);

  protected readonly today = startOfDay(new Date());
  protected readonly minDate = minSelectableDate(this.today);

  // Draft selection inside the popover; resets whenever the applied filters change.
  protected readonly draftFrom = linkedSignal<Date | null>(() => this.filters().from);
  protected readonly draftTo = linkedSignal<Date | null>(() => this.filters().to);

  protected readonly buttonLabel = computed(() => {
    const { preset, from, to } = this.filters();
    return DATE_PRESETS.find(p => p.key === preset)?.label ?? formatDateRange(from, to);
  });

  protected readonly draftLabel = computed(() => {
    const from = this.draftFrom();
    const to = this.draftTo();
    if (!from) return 'Select a start date';
    if (!to) return `${format(from, 'd MMM yyyy')} – Select an end date`;
    return formatDateRange(from, to);
  });

  protected toggle(): void {
    if (this.isOpen()) {
      this.close();
    } else {
      this.isOpen.set(true);
    }
  }

  protected close(): void {
    this.isOpen.set(false);
    this.draftFrom.set(this.filters().from);
    this.draftTo.set(this.filters().to);
  }

  protected selectPreset(key: DatePresetKey): void {
    this.filters.update(filters => ({ ...filters, ...presetRange(key, this.today) }));
    this.isOpen.set(false);
  }

  protected selectPlatform(platform: PlatformFilter): void {
    this.filters.update(filters => ({ ...filters, platform }));
  }

  protected selectLocation(locationId: string): void {
    this.filters.update(filters => ({ ...filters, locationId }));
  }

  protected selectSize(sizeKey: SizeKey | 'all'): void {
    this.filters.update(filters => ({ ...filters, sizeKey }));
  }

  protected apply(): void {
    const from = this.draftFrom();
    const to = this.draftTo();
    if (!from || !to) return;

    this.filters.update(filters => ({ ...filters, preset: 'custom', from, to }));
    this.isOpen.set(false);
  }

  protected onDocumentClick(event: MouseEvent): void {
    // composedPath() is captured at dispatch, so it still holds the host even if the
    // clicked element was re-rendered away before this document listener ran.
    if (this.isOpen() && !event.composedPath().includes(this.elementRef.nativeElement)) {
      this.close();
    }
  }
}
